import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'components/shared/DataTable';
import FilterSearchBar from 'components/shared/FilterSearchBar';
import FormInput from 'components/shared/BWFormControl/FormInput';
import Modal from 'components/shared/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import { deleteCombo, getComboOptions } from 'services/promotions.service';
import { defaultPaging, defaultParams, showToast } from 'utils/helpers';
import { formatPrice } from 'utils';
import { useDispatch } from 'react-redux';
import { showConfirmModal } from 'actions/global';
import styled from 'styled-components';
import moment from 'moment';
import ComboImportModal from './ComboImportModal';

const ProductLink = styled.a`
  color: #344054;
  text-decoration: none;

  &:hover {
    color: #ed3237;
    text-decoration: underline;
  }
`;

const ViewMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  height: 26px;
  margin-top: 2px;
  padding: 0 10px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #f2aaa5;
    color: #ed3237;
  }
`;

const ProductList = styled.div`
  display: grid;
  gap: 8px;
`;

const ProductItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  background: #fff;
`;

const ProductQuantity = styled.span`
  min-width: 46px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fff5f5;
  color: #ed3237;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
`;

const PriceCell = styled.div`
  b {
    color: #101828;
    font-weight: 800;
    white-space: nowrap;
  }

  p {
    margin-top: 3px;
    color: #667085;
    font-size: 11px;
    font-weight: 700;
  }
`;

const BrandCell = styled.div`
  max-width: 180px;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  white-space: normal;
`;

const DateCell = styled.div`
  white-space: nowrap;

  b {
    display: block;
    color: #344054;
    font-size: 12px;
  }

  span {
    display: block;
    margin-top: 2px;
    color: #667085;
    font-size: 11px;
  }
`;

const getProductId = (product) => product?.product_id?.value || product?.product_id;

const getProductUrl = (product) => {
  const productId = getProductId(product);
  return productId ? `/portal/product/detail/${productId}` : undefined;
};

const ProductNameLink = ({ product }) => {
  const productUrl = getProductUrl(product);
  const productText = `${product?.product_code || ''}${product?.product_code ? ' - ' : ''}${
    product?.product_name || ''
  }`;

  if (!productUrl) {
    return (
      <span>
        <b>{product?.product_code}</b>
        {product?.product_name ? <span>{` - ${product.product_name}`}</span> : null}
      </span>
    );
  }

  return (
    <ProductLink href={productUrl} target='_blank' rel='noreferrer' title='Mở chi tiết sản phẩm trong tab mới'>
      <b>{product?.product_code}</b>
      {product?.product_name ? <span>{` - ${product.product_name}`}</span> : null}
      <span className='bw_sr_only'>{productText}</span>
    </ProductLink>
  );
};

const renderProducts = (products = [], onViewAll) => {
  const visibleProducts = products.slice(0, 3);
  const remainCount = Math.max(products.length - visibleProducts.length, 0);

  return (
    <div className='bw_text_left'>
      {visibleProducts.map((product) => (
        <div key={`${product.combo_id}-${getProductId(product)}`} style={{ marginBottom: 4 }}>
          <ProductNameLink product={product} />
          <span style={{ color: '#667085' }}>{` x${product.quantity || 1}`}</span>
        </div>
      ))}
      {remainCount > 0 ? (
        <ViewMoreButton type='button' onClick={onViewAll}>
          {remainCount} sản phẩm +
        </ViewMoreButton>
      ) : null}
    </div>
  );
};

const renderComboPrice = (item) => {
  const price = Number(item?.combo_price || 0);
  if (!price) {
    return <span style={{ color: '#98a2b3', fontWeight: 700 }}>Chưa làm giá</span>;
  }

  return (
    <PriceCell>
      <b>{`${formatPrice(price, false, '.')} đ`}</b>
    </PriceCell>
  );
};

const getComboProductTotal = (item = {}, field) =>
  (item?.products || []).reduce((total, product) => total + Number(product?.quantity || 0) * Number(product?.[field] || 0), 0);

const parseComboCreatedDate = (value) => {
  if (!value) return null;
  const parsedDate = moment.parseZone(value);
  return parsedDate.isValid() ? parsedDate : null;
};

const renderComboBrands = (item) => {
  const brands = [
    ...new Set((item?.products || []).map((product) => product?.manufacture_name?.trim()).filter(Boolean)),
  ];

  return <BrandCell title={brands.join(', ')}>{brands.length ? brands.join(', ') : '-'}</BrandCell>;
};

const renderCreatedDate = (item) => {
  const createdDate = parseComboCreatedDate(item?.created_date);
  if (!createdDate) return '-';

  return (
    <DateCell>
      <b>{createdDate.format('DD/MM/YYYY')}</b>
      <span>{createdDate.format('HH:mm')}</span>
    </DateCell>
  );
};

const renderComboCost = (item) => {
  const cost = getComboProductTotal(item, 'cost_price');
  if (!cost) {
    return <span style={{ color: '#98a2b3', fontWeight: 700 }}>Chưa có giá vốn</span>;
  }

  return <b>{`${formatPrice(cost, false, '.')} đ`}</b>;
};

const renderComboProfit = (item) => {
  const salePrice = Number(item?.combo_price || 0);
  if (!salePrice) {
    return <span style={{ color: '#98a2b3', fontWeight: 700 }}>Chưa tính</span>;
  }
  const costPrice = getComboProductTotal(item, 'cost_price');
  const profit = salePrice - costPrice;
  const profitRate = salePrice > 0 ? Math.round((profit / salePrice) * 10000) / 100 : 0;
  const color = profit < 0 ? '#d92d20' : '#027a48';

  return (
    <PriceCell style={{ color }}>
      <b style={{ color }}>{`${formatPrice(profit, false, '.')} đ`}</b>
      <p style={{ color }}>{`${profitRate}%`}</p>
    </PriceCell>
  );
};

const PromotionCombosPage = () => {
  const dispatch = useDispatch();
  const methods = useForm();
  const defaultComboParams = {
    ...defaultParams,
    sort_column: 'created_date',
    sort_direction: 'desc',
  };
  const [params, setParams] = useState(defaultComboParams);
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState(defaultPaging);
  const [viewingCombo, setViewingCombo] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const { items, itemsPerPage, page, totalItems, totalPages } = dataList;

  const getData = useCallback(() => {
    setLoading(true);
    getComboOptions(params)
      .then((data) => {
        const items = Array.isArray(data?.items) ? [...data.items] : [];
        const sortedItems = items.sort((left, right) => {
          const leftTime = parseComboCreatedDate(left?.created_date)?.valueOf() || 0;
          const rightTime = parseComboCreatedDate(right?.created_date)?.valueOf() || 0;
          return rightTime - leftTime;
        });
        setDataList({
          ...(data || defaultPaging),
          items: sortedItems,
        });
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(getData, [getData]);

  const handleDelete = useCallback(
    async (listId = []) => {
      try {
        await deleteCombo({ list_id: listId });
        showToast.success('Xóa combo/Bộ sản phẩm thành công.');
        getData();
      } catch (error) {
        showToast.error(error?.message || 'Có lỗi xảy ra khi xóa combo.');
      }
    },
    [getData],
  );

  const confirmDelete = useCallback(
    (listId = []) => {
      dispatch(
        showConfirmModal(['Bạn có thực sự muốn xóa?', 'Bạn sẽ mất dữ liệu combo và danh sách thành phần.'], () =>
          handleDelete(listId),
        ),
      );
    },
    [dispatch, handleDelete],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Tên combo/Bộ sản phẩm',
        accessor: 'combo_name',
        formatter: (item) => (
          <div>
            <b>{item?.combo_name}</b>
            {item?.combo_code ? <p style={{ color: '#667085', marginTop: 4 }}>{item.combo_code}</p> : null}
          </div>
        ),
      },
      {
        header: 'Nhóm combo',
        accessor: 'combo_type_name',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
      },
      {
        header: 'Thương hiệu',
        formatter: renderComboBrands,
      },
      {
        header: 'Thành phần',
        formatter: (item) => renderProducts(item?.products || [], () => setViewingCombo(item)),
      },
      {
        header: 'Số SP',
        accessor: 'product_count',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
      },
      {
        header: 'Giá combo đang áp dụng',
        classNameHeader: 'bw_text_right',
        classNameBody: 'bw_text_right',
        formatter: renderComboPrice,
      },
      {
        header: 'Giá vốn',
        classNameHeader: 'bw_text_right',
        classNameBody: 'bw_text_right',
        formatter: renderComboCost,
      },
      {
        header: 'Lãi/Lỗ',
        classNameHeader: 'bw_text_right',
        classNameBody: 'bw_text_right',
        formatter: renderComboProfit,
      },
      {
        header: 'Trạng thái',
        accessor: 'is_active',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        formatter: (item) =>
          item?.is_active ? (
            <span className='bw_label_outline bw_label_outline_success'>Kích hoạt</span>
          ) : (
            <span className='bw_label_outline bw_label_outline_danger'>Ẩn</span>
          ),
      },
      {
        header: 'Ngày tạo',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        formatter: renderCreatedDate,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        globalAction: true,
        icon: 'fi fi-rr-plus',
        type: 'success',
        content: 'Thêm mới',
        permission: 'PROMOTION_COMBO_ADD',
        onClick: () => window._$g.rdr('/promotion-combos/add'),
      },
      {
        globalAction: true,
        icon: 'fi fi-rr-inbox-in',
        type: 'success',
        outline: true,
        content: 'Import Excel',
        permission: 'PROMOTION_COMBO_ADD',
        onClick: () => setImportOpen(true),
      },
      {
        icon: 'fi fi-rr-edit',
        color: 'blue',
        title: 'Chỉnh sửa',
        permission: 'PROMOTION_COMBO_EDIT',
        onClick: (item) => window._$g.rdr(`/promotion-combos/edit/${item?.combo_id}`),
      },
      {
        icon: 'fi fi-rr-tags',
        color: 'green',
        title: 'Làm giá combo',
        permission: 'SL_PRICES_ADD',
        onClick: (item) => window._$g.rdr(`/prices/combo/add/${item?.combo_id}`),
      },
      {
        icon: 'fi fi-rr-trash',
        color: 'red',
        title: 'Xóa',
        permission: 'PROMOTION_COMBO_DEL',
        onClick: (item) => confirmDelete([item?.combo_id]),
      },
    ],
    [confirmDelete],
  );

  return (
    <div className='bw_main_wrapp'>
      <FormProvider {...methods}>
        <FilterSearchBar
          title='Tìm kiếm'
          onSubmit={() =>
            setParams((prev) => ({
              ...prev,
              page: 1,
              search: methods.watch('search'),
            }))
          }
          onClear={() => {
            methods.reset({ search: '' });
            setParams(defaultComboParams);
          }}
          actions={[
            {
              title: 'Từ khóa',
              component: <FormInput field='search' placeholder='Nhập tên combo, mã combo hoặc URL' />,
            },
          ]}
        />
      </FormProvider>
      <DataTable
        loading={loading}
        columns={columns}
        data={items}
        actions={actions}
        fieldCheck='combo_id'
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        page={page}
        totalItems={totalItems}
        onChangePage={(nextPage) =>
          setParams((prev) => ({
            ...prev,
            page: nextPage,
          }))
        }
        handleBulkAction={(selectedItems, action) => {
          if (action === 'delete') {
            confirmDelete((selectedItems || []).map((item) => item?.combo_id));
          }
        }}
      />
      <ComboImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => getData()}
      />
      {viewingCombo ? (
        <Modal
          open={Boolean(viewingCombo)}
          witdh='720px'
          header={`Thành phần combo: ${viewingCombo?.combo_name || ''}`}
          lalbelClose='Đóng'
          onClose={() => setViewingCombo(null)}>
          <ProductList>
            {(viewingCombo?.products || []).map((product) => (
              <ProductItem key={`${product.combo_id}-${getProductId(product)}`}>
                <ProductNameLink product={product} />
                <ProductQuantity>{`x${product.quantity || 1}`}</ProductQuantity>
              </ProductItem>
            ))}
          </ProductList>
        </Modal>
      ) : null}
    </div>
  );
};

export default PromotionCombosPage;
